import { KoreanMainContent, OtherMainContent } from '@/components/feature/parking';
import { FloorGuideType } from '@/enums/floor-guide';
import { useOrientation } from '@/hooks';
import useBaseSize from '@/hooks/useBaseSize';
import { PATHS } from '@/router/paths';
import type { CarParkingListRequest, ParkingFeeRequest, ParkingRecord } from '@/services/dto/parking';
import { useAreaInfo, useCarParkingList, useParkingFee, useParkingInfo } from '@/services/queries';
import { useConfigStore } from '@/stores';
import { useFloorGuideStore } from '@/stores/floorGuideStore';
import { generateFloorName } from '@/utils/floor-guide';
import { getHSize, getSize } from '@/utils/size';
import clsx from 'clsx';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './ParkingPage.module.scss';
import { STORE_CODE_TO_PARKING_BRANCH } from '@/constants/parking/branch-parking-info';

export default function ParkingPage() {
    const config = useConfigStore((s) => s.config);
    const { setSelectedParkingSlot, setFloorGuideType } = useFloorGuideStore();

    const navigate = useNavigate();
    const { screenMode, language } = config.ui;
    const { server } = config;
    const { store, buildingMenus } = server || {};
    const parkingTitle = buildingMenus?.[language]?.find((item) => item.menuCode === 'PARKING')?.name || '';
    const parkingDescription = buildingMenus?.[language]?.find((item) => item.menuCode === 'PARKING')?.description || '';
    const storeCode = store?.storeCode || '';
    const isKorean = language === 'ko';
    const hasParkingBranch = !!STORE_CODE_TO_PARKING_BRANCH[storeCode];

    const [searchState, setSearchState] = useState({
        query: '',
        showResults: false,
        selectedResult: null as ParkingRecord | null,
        searchRequest: null as CarParkingListRequest | null,
    });
    const [parkingFeeRequest, setParkingFeeRequest] = useState<ParkingFeeRequest | null>(null);
    const { isHorizontal } = useOrientation();
    const { data: parkingData } = useParkingInfo(storeCode, isKorean);
    const { baseWidth, baseHeight } = useBaseSize();

    const resetSearchState = () => {
        setSearchState({
            query: '',
            showResults: false,
            selectedResult: null,
            searchRequest: null,
        });
    };
    //API call to get car parking list
    const { data: parkingApiData, isLoading: isSearchLoading } = useCarParkingList(searchState.searchRequest || { siteCode: '', carNumber: '' });

    //API call to get parking fee
    const { data: parkingFeeApiData } = useParkingFee(parkingFeeRequest || { siteCode: '', licensePlate: '', inGate: '', inDate: '', inTime: '' });

    //API call to get area info
    const { data: areaInfoData } = useAreaInfo(
        parkingFeeRequest ? { siteCode: parkingFeeRequest.siteCode, licensePlate: parkingFeeRequest.licensePlate } : { siteCode: '', licensePlate: '' }
    );
    const areaInfo = areaInfoData?.data?.resultBody;
    const isUseLocation = areaInfo?.parkArea && areaInfo?.parkStair ? true : false;

    const handleSearch = (query: string) => {
        const request = {
            siteCode: storeCode,
            carNumber: query,
        };
        

        setSearchState((prev) => ({
            ...prev,
            query,
            showResults: true,
            selectedResult: null,
            searchRequest: request,
        }));
    };

    const handleClearAll = () => {
        resetSearchState();
    };

    const handleBackspace = () => {
        if (searchState.query.length <= 1) {
            resetSearchState();
        } else {
            setSearchState((prev) => ({
                ...prev,
                query: prev.query.slice(0, -1),
                showResults: false,
                selectedResult: null,
                searchRequest: null,
            }));
        }
    };

    const handleCardClick = (result: ParkingRecord) => {
        setSearchState((prev) => ({ ...prev, selectedResult: result }));

        // Call parking fee API
        const feeRequest: ParkingFeeRequest = {
            siteCode: result.siteCode,
            licensePlate: result.licenseplate,
            inGate: result.inGate,
            inDate: result.inDate,
            inTime: result.inTime,
        };
        setParkingFeeRequest(feeRequest);
    };

    const handleBackFromDetail = () => {
        resetSearchState();
    };

    const handleDirectionGuide = () => {
        const allFloors = store?.buildings?.flatMap((building) => building.floors) || [];
        const normalizedParkStair = generateFloorName(areaInfo?.parkStair || '').toLowerCase();
        const floorCode = allFloors.find(
            (floor) => generateFloorName(floor.name[language]).toLowerCase() === normalizedParkStair
        )?.floorCode;
        setSelectedParkingSlot({ studioFloorID: floorCode, parkingSlotTitle: areaInfo?.parkArea });
        setFloorGuideType(FloorGuideType.DIRECTION_FROM_PARKING);
        navigate(PATHS.FLOOR_GUIDE.DIRECTION_GUIDE_FROM_PARKING(areaInfo?.parkArea || '', floorCode || ''));
    };

    const isSplitMode = screenMode !== 'fullscreen';
    const splitOffset = isSplitMode ? (isHorizontal ? getHSize(171, baseHeight) : getHSize(868, baseHeight)) : undefined;

    return (
        <div className={clsx(styles.parking, 'h-full w-full')}>
            {isSplitMode && <div aria-hidden="true" style={{ height: splitOffset, flexShrink: 0 }} />}
            <div
                className={clsx(styles['parking__content'], 'flex w-full flex-col')}
                style={{
                    ...(isSplitMode
                        ? {
                              height: isHorizontal ? getHSize(829, baseHeight) : getHSize(872, baseHeight),
                              maxWidth: getSize(1920, baseWidth),
                              overflow: 'hidden',
                          }
                        : { height: '100%' }),
                }}
            >
            {/* Header */}
            <header
                data-region-nav
                tabIndex={1}
                data-region-order={1}
                className={`widescreen:pt-[calc(56*var(--vh-unit))] widescreen:pb-[calc(14*var(--vh-unit))] widescreen:fullscreen:pb-[calc(14*var(--vh-unit))] fullscreen:pb-[calc(64*var(--vh-unit))] widescreen:pl-[calc(67*var(--vw-unit))] widescreen:pr-[calc(68*var(--vw-unit))] widescreen:gap-[calc(16*var(--vh-unit))] widescreen:border-none border-border-transparent-to-f9 focus-border flex flex-col gap-[calc(10*var(--vh-unit))] border-b-[calc(1*var(--vh-unit))] px-[calc(56*var(--vw-unit))] pt-[calc(86*var(--vh-unit))] pb-[calc(24*var(--vh-unit))] text-center`}
                aria-label={parkingTitle + ' ' + parkingDescription}
                data-interactive
            >
                <h1 className="widescreen:text-[calc(56*var(--vh-unit))] widescreen:font-medium widescreen:leading-[calc(60*var(--vh-unit))] truncate text-center text-[calc(60*var(--vh-unit))] leading-[calc(90*var(--vh-unit))] font-bold text-[color:var(--color-text-main01)]">
                    {parkingTitle}
                </h1>
                <p className="widescreen:text-[calc(26*var(--vh-unit))] widescreen:font-medium widescreen:leading-[calc(30*var(--vh-unit))] truncate text-center text-[calc(28*var(--vh-unit))] leading-[calc(30*var(--vh-unit))] font-medium text-[color:var(--color-text-main01)]">
                    {parkingDescription}
                </p>
            </header>
            {isKorean && hasParkingBranch ? (
                <KoreanMainContent
                    searchState={searchState}
                    handleBackFromDetail={handleBackFromDetail}
                    handleCardClick={handleCardClick}
                    handleSearch={handleSearch}
                    handleClearAll={handleClearAll}
                    handleBackspace={handleBackspace}
                    isUseLocation={isUseLocation}
                    areaInfo={areaInfo || null}
                    parkingFeeApiData={parkingFeeApiData || null}
                    parkingApiData={parkingApiData || null}
                    isSearchLoading={isSearchLoading}
                    parkingData={parkingData || null}
                    isHorizontal={isHorizontal}
                    screenMode={screenMode}
                    handleDirectionGuide={handleDirectionGuide}
                    storeCode={storeCode}
                />
            ) : (
                <OtherMainContent storeCode={storeCode} />
            )}
            </div>
        </div>
    );
}
